import { defineStore } from 'pinia';
import * as Sentry from '@sentry/vue';
import type {
  GamificationSlug,
  PlayerGamifications,
  PlayerVipLevel,
} from '~/types/player-profile';
import type { PlayerProfileErrorKind } from '~/adapters/api-player-profile/player-profile-api';
import {
  clearPlayerProfileCache,
  fetchPlayerProfileHandler,
  readPlayerProfileCache,
} from '~/handlers/fetch-player-profile.handler';

// O que se sabe do jogador logado (GET /players/me): nível VIP, nível CRM e a
// elegibilidade por gamificação. Existe separado da store do check-in porque a
// resposta é sobre a CONTA, não sobre o modal — e porque quem depende dela (gate
// da roletinha, da loja, do promocode, card de VIP do Clube Fidelidade) precisa
// dela sem abrir nada.
//
// Antes a pergunta pegava carona em dois lugares: o board do check-in (o menu
// pedia período, trilha e claims inteiros só pra ler um booleano) e o perfil da
// Smartico (o tier saía das `core_public_tags`, o que exigia o SDK carregado e o
// `identify` concluído). Agora as duas respostas vêm da mesma rota, do tamanho da
// pergunta.
//
// Quem dispara e quem invalida é o plugin de sessão
// (plugins/player-profile.client.ts), não um componente: o dado é da sessão e não
// pode existir só enquanto uma tela específica estiver montada.

export const usePlayerProfileStore = defineStore('player-profile', () => {
  const vip = ref<PlayerVipLevel | null>(null);

  // Slug do nível CRM como o CORE o guarda, e SÓ quando veio de resposta fresca
  // do /players/me (o cache de sessionStorage não hidrata isto — ver handler).
  // É o lado "armazenado" da regra de divergência do sync-level, e é também o
  // "você está no X" da copy do aviso de nível.
  const crmLevel = ref<string | null>(null);

  // Veredito por gamificação (`gamifications` do /players/me), SÓ de resposta
  // fresca — o cache desta aba não hidrata isto, porque um "não" velho
  // trancaria um modal de quem subiu de nível depois. Guarda junto o ID de quem
  // foi perguntado: no SPA dá pra trocar de login sem recarregar, e um mapa solto
  // viajaria pro próximo jogador.
  //
  // `null` com `gamificationsUserId` preenchido = o core respondeu, mas o mapa
  // veio quebrado (ver mapper). É "não sei", e "não sei" não nega.
  const gamifications = ref<PlayerGamifications | null>(null);
  const gamificationsUserId = ref<number | null>(null);

  // Última conta cujo perfil já foi RESPONDIDO (pelo core, ou pelo cache desta
  // aba enquanto a resposta não chega). Serve pra distinguir "não" de "ainda não
  // sei", que são coisas diferentes pra quem lê: enquanto a resposta não chega,
  // todo mundo parece jogador comum.
  const knownUserId = ref<number | null>(null);

  const { userId, fetchUserProfile } = useUserData();

  // Reações a falha do /players/me que só podem acontecer UMA vez por conta na
  // sessão — o `ensure()` é chamado no login, em toda volta de foco e a cada
  // abertura do Clube Fidelidade, e nenhuma dessas reações pode escalar com isso.
  const reauthCheckedUserId = ref<number | null>(null);
  const retryScheduledUserId = ref<number | null>(null);

  const isVip = computed(() => vip.value !== null);

  // "Já tenho resposta pra ESTA conta." Quem só enfeita (chip, selo) pode
  // ignorar; quem decide layout precisa distinguir.
  const isProfileKnown = computed(
    () => !!userId.value && knownUserId.value === userId.value,
  );

  // "O core já me deu o mapa de gamificações desta conta, e ele veio legível."
  // É ISTO — e não `isProfileKnown` — que autoriza alguém a NEGAR acesso: o
  // perfil pode ser conhecido pelo cache (só `vip`) sem que haja veredito nenhum.
  const isGamificationsKnown = computed(
    () =>
      !!userId.value &&
      gamificationsUserId.value === userId.value &&
      gamifications.value !== null,
  );

  /**
   * Sinal POSITIVO: só é elegível quem tem um `true` explícito pra ESTA conta e
   * ESTE slug. Deslogado, resposta ausente, mapa quebrado, slug ausente — tudo
   * isso é "não", porque um "não sei" não pode virar convite (menu, badge).
   */
  function isEligibleFor(slug: GamificationSlug): boolean {
    if (!isGamificationsKnown.value) return false;

    return gamifications.value?.[slug]?.isEligible === true;
  }

  /**
   * Sinal NEGATIVO, o único que autoriza a trancar um modal: o core respondeu o
   * mapa desta conta e nele este slug NÃO é `true` (inclusive ausente, que é a
   * gamificação desativada no backoffice). Num "não sei" — perfil em trânsito,
   * requisição falhada, core offline, mapa quebrado — responde `false`: o modal
   * abre normal e quem dá a palavra final é o core, no POST da feature. Negar por
   * falta de resposta tiraria a promoção de quem tem direito a ela.
   */
  function isKnownIneligibleFor(slug: GamificationSlug): boolean {
    if (!isGamificationsKnown.value) return false;

    return gamifications.value?.[slug]?.isEligible !== true;
  }

  /** Slug CRM do degrau mínimo da gamificação, pra copy do aviso. */
  function minLevelFor(slug: GamificationSlug): string | null {
    if (!isGamificationsKnown.value) return null;

    return gamifications.value?.[slug]?.minLevel ?? null;
  }

  /**
   * Garante que se sabe o estado do jogador. A pergunta VAI ao core TODA VEZ —
   * não há freio de tempo nem memo que a engula; contra chamadores simultâneos
   * sobra só o compartilhamento da requisição em vôo, dentro do handler.
   *
   * Antes de esperar, hidrata do cache desta aba (última resposta conhecida) pra
   * a tela não piscar no F5 — quem lê `vip` monta o card certo de cara. O que o
   * core responder sobrescreve isso, inclusive pra baixo.
   */
  async function ensure() {
    const id = userId.value;

    if (!id) return;

    // Só hidrata quem ainda não tem resposta pra ESTA conta: numa chamada
    // seguinte (foco de aba) o que está na store já é mais fresco que o cache.
    // O cache só traz `vip` — nível e elegibilidade continuam "não sei".
    if (knownUserId.value !== id) {
      const cached = readPlayerProfileCache(id);

      if (cached) {
        vip.value = cached.vip;
        knownUserId.value = id;
      }
    }

    const { profile, failure } = await fetchPlayerProfileHandler({
      userId: id,
    });

    // A conta pode ter trocado enquanto a resposta vinha; o veredito é de quem
    // foi perguntado.
    if (userId.value !== id) return;

    // A busca falhou. Preserva o que já sabia — silêncio não rebaixa jogador —
    // e reage à CAUSA, porque desde a introspecção do token no core elas não
    // são mais equivalentes.
    if (!profile) {
      handleFailure(id, failure);

      return;
    }

    vip.value = profile.vip;
    crmLevel.value = profile.level;
    gamifications.value = profile.gamifications;
    gamificationsUserId.value = id;
    knownUserId.value = id;
  }

  /**
   * O que cada falha do /players/me pede além de "preservar o que se sabia".
   *
   * - 'unauthenticated' (401): o core introspectou o token na Cactus e ela NÃO o
   *   reconhece. Isso é um veredito sobre a SESSÃO, não sobre o perfil — mas não
   *   deslogamos por conta do core: um bug de configuração lá (brand errada,
   *   credencial vencida) derrubaria todo mundo. Quem julga a sessão é a
   *   plataforma: forçamos o /auth/user-profile, cujo fluxo já desloga com o
   *   toast de "sessão expirada" se o token estiver morto de verdade, e não faz
   *   nada se ele estiver vivo. Uma vez por conta — se a sessão está viva, não
   *   há o que reperguntar; se está morta, o logout troca a conta e zera a marca.
   *
   * - 'unavailable' (503): o core não conseguiu perguntar à Cactus e faz
   *   fail-closed com cooldown de 10s. O refetch por foco (≥30s escondido) já
   *   cobre a volta; aqui só adiantamos UMA retentativa depois do cooldown, pra
   *   quem logou durante o soluço não ficar sem VIP/elegibilidade até alternar
   *   de aba. Uma vez por conta: não é polling.
   *
   * - 'network' / 'api': nada além do que já se fazia (o foco repergunta). Core
   *   fora do ar cai aqui: nenhum modal tranca, a loja lista normal.
   */
  function handleFailure(id: number, failure: PlayerProfileErrorKind) {
    if (failure === 'unauthenticated') {
      if (reauthCheckedUserId.value === id) return;

      reauthCheckedUserId.value = id;

      Sentry.captureMessage('players/me recusou o token do jogador (401)', {
        level: 'warning',
        tags: { source: 'player-profile', kind: failure },
        extra: { userId: id },
      });

      // Sem await: quem decide (e desloga) é o fluxo do user-profile.
      fetchUserProfile({ force: true }).catch(() => {});

      return;
    }

    if (failure === 'unavailable') {
      if (retryScheduledUserId.value === id) return;

      retryScheduledUserId.value = id;

      // 15s: acima do cooldown de 10s do core, pra não bater na janela em que
      // ele ainda responde 503 sem nem tentar.
      setTimeout(() => {
        if (userId.value === id) ensure();
      }, 15_000);
    }
  }

  /**
   * Aceita o slug final que o POST sync-level devolveu. Sobrescrever aqui é o
   * que faz a divergência recém-regularizada parar de existir aos olhos de quem
   * a observa — sem esperar o próximo /players/me.
   *
   * O mapa de gamificações NÃO é recalculado aqui: ele é função do nível, mas a
   * régua de cada feature mora no core. O próximo `ensure()` (foco, hub) traz o
   * veredito novo.
   */
  function acceptSyncedLevel(id: number, level: string | null) {
    if (!id || userId.value !== id) return;

    crmLevel.value = level;
  }

  /** Troca de conta / logout: o que se sabia era daquele jogador. */
  function reset(previousUserId?: number) {
    vip.value = null;
    crmLevel.value = null;
    gamifications.value = null;
    gamificationsUserId.value = null;
    knownUserId.value = null;
    reauthCheckedUserId.value = null;
    retryScheduledUserId.value = null;
    clearPlayerProfileCache(previousUserId);
  }

  return {
    vip,
    crmLevel,
    gamifications,
    isVip,
    isProfileKnown,
    isGamificationsKnown,
    isEligibleFor,
    isKnownIneligibleFor,
    minLevelFor,
    ensure,
    acceptSyncedLevel,
    reset,
  };
});
